<template>
  <div v-if="reservation" class="qr-details">
    <header :class="['qr-header', statusClass]">
      <div>
        <p class="qr-eyebrow">Escaneo exitoso</p>
        <h2>{{ reservation.name }}</h2>
        <p class="qr-contact">{{ reservation.email || 'Sin email registrado' }}</p>
        <p class="qr-table muted" v-if="reservation.recordType === 'walkin'">Walk-in</p>
        <p class="qr-table" v-if="reservation.tableNumber">
          Mesa {{ reservation.tableNumber }}
        </p>
        <p class="qr-table muted" v-else>Sin mesa asignada</p>
      </div>
      <span class="qr-status-pill">{{ statusLabel }}</span>
    </header>

    <section class="qr-grid">
      <article class="qr-block">
        <label>{{ reservation.recordType === 'walkin' ? 'Llegada' : 'Fecha y hora' }}</label>
        <p>{{ formatDate(reservation.arrivalDate || reservation.date) }} · {{ formatTime(reservation.arrivalTime || reservation.time) }}</p>
      </article>
      <article class="qr-block">
        <label>Personas</label>
        <p>{{ reservation.people }} {{ reservation.people === 1 ? 'persona' : 'personas' }}</p>
      </article>
      <article class="qr-block">
        <label>Teléfono</label>
        <p>{{ reservation.phone || 'Sin teléfono' }}</p>
      </article>
    </section>

    <section class="qr-grid">
      <article class="qr-block" v-if="reservation.paymentStatus">
        <label>Pago</label>
        <p>{{ reservation.paymentStatus }}</p>
      </article>
      <article class="qr-block" v-if="reservation.cloverPaymentId">
        <label>Payment ID</label>
        <p>{{ reservation.cloverPaymentId }}</p>
      </article>
      <article class="qr-block" v-if="reservation.cloverOrderId">
        <label>Order ID</label>
        <p>{{ reservation.cloverOrderId }}</p>
      </article>
      <article class="qr-block" v-if="reservation.paymentCapturedAmount">
        <label>Cobrado</label>
        <p>{{ formatCurrency(reservation.paymentCapturedAmount, reservation.paymentCapturedCurrency) }}</p>
      </article>
    </section>

    <section class="qr-grid">
      <article class="qr-block">
        <label>Mesa asignada</label>
        <p v-if="reservation.tableNumber">
          {{ reservation.tableSection ? `${reservation.tableSection} · ` : '' }}Mesa {{ reservation.tableNumber }}
        </p>
        <p v-else>Sin mesa asignada</p>
      </article>
      <article class="qr-block">
        <label>Grupo</label>
        <p>{{ reservation.tableGroupName || 'Sin grupo' }}</p>
      </article>
      <article class="qr-block">
        <label>Mesero asignado</label>
        <p>{{ reservation.waiterName || reservation.tableWaiter || 'Sin asignar' }}</p>
      </article>
    </section>

    <section class="qr-notes" v-if="reservation.specialNotes">
      <label>Comentarios del cliente</label>
      <p>{{ reservation.specialNotes }}</p>
    </section>

    <section class="qr-alert" v-if="reservation.status === 'used'">
      <span>⚠️ Reservación ya escaneada</span>
      <p>Esta reservación fue marcada como utilizada anteriormente.</p>
    </section>
  </div>
  <div v-else class="qr-empty">
    <p>No hay información del QR.</p>
  </div>
</template>

<script>
import { parseLocalDate } from '../utils/date';
import { formatTimeLabel } from '../utils/time';

export default {
  name: 'QRCodeView',
  props: {
    reservation: {
      type: Object,
      default: null
    }
  },
  computed: {
    statusClass() {
      const status = (this.reservation?.status || '').toLowerCase();
      if (status === 'confirmed') return 'is-confirmed';
      if (status === 'pending') return 'is-pending';
      if (status === 'used') return 'is-used';
      return 'is-cancelled';
    },
    statusLabel() {
      return (this.reservation?.status || 'sin estado').toUpperCase();
    }
  },
  methods: {
    formatTime(time) {
      return formatTimeLabel(time) || '--:--';
    },
    formatDate(dateString) {
      if (!dateString) return 'Sin fecha';
      const date = parseLocalDate(dateString);
      if (!date) return dateString;
      return date.toLocaleDateString('es-PR', { weekday: 'long', month: 'short', day: 'numeric' });
    },
    formatCurrency(amount, currency = 'USD') {
      return new Intl.NumberFormat('es-PR', {
        style: 'currency',
        currency: String(currency || 'USD').trim().toUpperCase() || 'USD'
      }).format((Number(amount) || 0) / 100);
    }
  }
};
</script>

<style scoped>
.qr-details {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.qr-header {
  border-radius: 20px;
  padding: 20px;
  color: #0f172a;
  display: flex;
  justify-content: space-between;
  gap: 16px;
  align-items: center;
}

.qr-header.is-confirmed {
  background: linear-gradient(135deg, rgba(36, 216, 134, 0.25), rgba(7, 94, 84, 0.4));
}

.qr-header.is-pending {
  background: linear-gradient(135deg, rgba(250, 204, 21, 0.25), rgba(146, 64, 14, 0.35));
}

.qr-header.is-used {
  background: linear-gradient(135deg, rgba(14, 165, 233, 0.25), rgba(30, 64, 175, 0.35));
}

.qr-header.is-cancelled {
  background: linear-gradient(135deg, rgba(248, 113, 113, 0.25), rgba(185, 28, 28, 0.35));
}

.qr-eyebrow {
  text-transform: uppercase;
  font-size: 12px;
  letter-spacing: 0.25em;
  margin: 0 0 4px;
}

.qr-contact {
  margin: 4px 0 0;
  opacity: 0.8;
}

.qr-table {
  margin: 4px 0 0;
  font-weight: 600;
  color: #0f172a;
}

.qr-table.muted {
  font-weight: 500;
  color: rgba(15, 23, 42, 0.6);
}

.qr-status-pill {
  background: rgba(15, 23, 42, 0.15);
  border-radius: 999px;
  padding: 6px 14px;
  font-weight: 600;
  color: #0f172a;
}

.qr-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 12px;
}

.qr-block {
  border: 1px solid rgba(15, 23, 42, 0.1);
  border-radius: 16px;
  padding: 12px 16px;
  background: #fff;
}

.qr-block label {
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: rgba(15, 23, 42, 0.6);
}

.qr-block p {
  margin: 4px 0 0;
  font-size: 16px;
  color: #0f172a;
}

.qr-notes {
  border-radius: 16px;
  padding: 14px 18px;
  background: rgba(15, 23, 42, 0.03);
  border: 1px dashed rgba(15, 23, 42, 0.2);
}

.qr-notes label {
  font-weight: 600;
  font-size: 13px;
  color: rgba(15, 23, 42, 0.7);
}

.qr-notes p {
  margin: 6px 0 0;
  color: #0f172a;
}

.qr-alert {
  border-radius: 12px;
  border: 1px solid rgba(185, 28, 28, 0.4);
  background: rgba(248, 113, 113, 0.15);
  padding: 12px 16px;
  color: #7f1d1d;
}

.qr-alert span {
  font-weight: 600;
}

.qr-empty {
  text-align: center;
  color: rgba(15, 23, 42, 0.6);
}
</style>
